import classnames from 'classnames/bind'
import { useState } from 'react'
import { FormProvider } from 'react-hook-form'
import { useTranslate } from 'react-polyglot'
import { GlobalThemeColors } from '~/@types/colors'
import { Variant } from '~/@types/product'
import { GlobalTextPreset } from '~/@types/text-preset'
import { GlobalTextStyling } from '~/@types/text-styling'
import { shopifyVariantDefaultTitle } from '~/lib/constants'

import { useInputPreset } from '~/components/Abstracts/Form/Input/hooks'
import RichText, { RichTextBlock } from '~/components/Abstracts/RichText'
import ErrorList from '~/components/Form/ErrorList'
import Input from '~/components/Form/Input'
import BlurredCta from '~/components/UI/BlurredCta'
import Image from '~/components/UI/Image'
import PopIn, { PopinHeader } from '~/components/UI/PopIn'
import { ProductHeaderProps } from '~/components/UI/ProductHeader'

import { useGlobalData } from '~/providers/GlobalDataProvider'
import { usePopIn } from '~/providers/PopInProvider'
import RecaptchaHandler from '~/providers/RecaptchaProvider/RecaptchaHandler'
import { useStyle } from '~/providers/StyleProvider'

import useFormWithMutation from '~/hooks/useFormWithMutation'
import useLocale from '~/hooks/useLocale'
import useStockAlertMutation from '~/hooks/useStockAlertMutation'

import { getBase64IDFromAdminGID } from '~/utils/shopify-ids'

import { CTA, FORM } from '~/data/dictionary'
import serializePrice from '~/data/serialize-price'

import css from './styles.module.scss'

const cx = classnames.bind(css)

export interface AlertStockPopinProps {
  className?: string
  header?: string
  confirmation?: string
  text?: RichTextBlock
  variantId?: string
  backupSubtitle?: string
  productImage: string
  redirectLink: string
  productName?: string
  onSuccess?(): void
  variant?: Variant
}

export interface PopInStockAlertProps {
  stockAlertProps?: ProductHeaderProps['stockAlertProps']
  currentVariant?: Variant
  withPrev?: boolean
}
export function PopInStockAlert({
  stockAlertProps,
  currentVariant,
  withPrev = false,
}: PopInStockAlertProps) {
  const {
    global: { alertStock },
  } = useGlobalData()

  return (
    <PopIn
      className={cx(css.popin)}
      isCloseButton={true}
      withPrev={withPrev}
      popinHeader={
        <PopinHeader
          title={alertStock.header?.replace(
            '%{value}',
            `${stockAlertProps.productName}`,
          )}
        />
      }>
      {currentVariant && stockAlertProps && (
        <AlertStockPopin
          className={cx(css.alertStock)}
          variantId={currentVariant?.id}
          variant={currentVariant}
          {...stockAlertProps}
        />
      )}
    </PopIn>
  )
}

function AlertStockPopin({
  className,
  onSuccess,
  variantId,
  productName,
  variant,
  backupSubtitle,
  ...stockAlertProps
}: AlertStockPopinProps) {
  const {
    global: { alertStock },
  } = useGlobalData()

  const t = useTranslate()
  const locale = useLocale()
  const [alertRegistered, setAlertRegistered] = useState(false)
  const { mutate, isLoading } = useStockAlertMutation(
    {
      onSuccess: () => {
        setAlertRegistered(true)
        onSuccess?.()
      },
    },
    (...params) => form.setError(...params),
  )

  const handleSubmit = ({ email }) => {
    mutate({
      email,
      productId: getBase64IDFromAdminGID(variantId),
      productName,
      ...stockAlertProps,
    })
  }

  const { form, onSubmit } = useFormWithMutation(
    handleSubmit,
    null,
    'alertStock',
  )

  const props = useInputPreset('email', {
    placeholder: t(FORM.NEWSLETTER_EMAIL),
  })

  const { removeAll } = usePopIn()

  const productNameStyle = useStyle({
    textPreset: GlobalTextPreset.Title12Primary,
    textStyling: GlobalTextStyling.UpperCase,
    color: GlobalThemeColors.Shade1Darker,
  })

  const textStyle = useStyle({
    textPreset: GlobalTextPreset.Input16Primary,
  })

  const productPriceStyle = useStyle({
    textPreset: GlobalTextPreset.Text12Secondary,
    color: GlobalThemeColors.Black,
  })

  const productTitleStyle = useStyle({
    textPreset: GlobalTextPreset.Label12Secondary,
    color: GlobalThemeColors.Shade2,
    textStyling: GlobalTextStyling.UpperCase,
  })

  const subTitle =
    variant?.title !== shopifyVariantDefaultTitle
      ? variant.title
      : backupSubtitle ?? null

  return (
    <div className={cx(css.AlertStockPopin, className)}>
      <div className={cx(css.variantExcerpt)}>
        {variant?.image && (
          <Image
            asPlaceholder
            loading="lazy"
            objectFit="cover"
            ratio={0.752}
            sizes="94px"
            layout="fill"
            loader={null}
            className={css.image}
            {...variant?.image}
          />
        )}
        <div className={cx(css.variantContent)}>
          {productName && (
            <span className={cx(css.productName, productNameStyle)}>
              {productName}
            </span>
          )}
          {variant?.price && (
            <span className={cx(css.price, productPriceStyle)}>
              {serializePrice(
                locale,
                variant.price.currencyCode,
                variant.price.amount,
              )}
            </span>
          )}
          {subTitle && (
            <span className={cx(css.title, productTitleStyle)}>{subTitle}</span>
          )}
        </div>
      </div>
      <div className={css.content}>
        {!alertRegistered && (
          <RichText
            className={cx(css.text, textStyle)}
            render={alertStock?.text}
          />
        )}
        {alertRegistered && alertStock?.confirmation && (
          <p className={cx(textStyle)}>{alertStock?.confirmation}</p>
        )}
      </div>

      <RecaptchaHandler>
        <FormProvider {...form}>
          <form onSubmit={onSubmit}>
            <div className={cx(css.inputContainer)}>
              {!alertRegistered && (
                <>
                  <Input
                    className={cx(textStyle)}
                    theme={GlobalThemeColors.Black}
                    {...props}
                  />
                  <ErrorList className={cx(css.errorList)} />

                  <BlurredCta
                    className={cx(css.button)}
                    isLoading={isLoading}
                    isBig
                    theme={GlobalThemeColors.Black}
                    withBackground
                    withBorder={false}
                    onClick={onSubmit}>
                    {t(FORM.SUBMIT)}
                  </BlurredCta>
                </>
              )}
              {alertRegistered && (
                <>
                  <BlurredCta
                    className={cx(css.button)}
                    isLoading={isLoading}
                    isBig
                    theme={GlobalThemeColors.Black}
                    withBackground
                    withBorder={false}
                    onClick={removeAll}>
                    {t(CTA.CLOSE)}
                  </BlurredCta>
                </>
              )}
            </div>
          </form>
        </FormProvider>
      </RecaptchaHandler>
    </div>
  )
}

AlertStockPopin.defaultProps = {}

export default AlertStockPopin
